import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Plus,
  CalendarIcon,
  Trash2,
  Edit2,
  Search,
  Filter,
  Loader2,
  MessageSquare,
  ExternalLink,
  ClipboardList,
  ChevronLeft,
  ChevronRight,
  Check,
  Users,
  AlertTriangle,
  Target,
  Infinity as InfinityIcon
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import ConfirmDialog from '../components/ConfirmDialog';
import StagedAttachmentPicker from '../components/tasks/StagedAttachmentPicker';
import { DriveLinksEditor, DriveLinksList } from '../components/tasks/DriveLinks';
import { uploadTaskAttachment } from '../services/attachments';
import {
  DEFAULT_NUMERIC_CONFIG,
  NUMERICAL_CURRENCY_OPTIONS,
  TASK_TYPE_NUMERICAL,
  TASK_TYPE_STANDARD,
  buildNumericConfigPayload,
  clampProgressPercent,
  formatNumericValue,
  formatProgressPercent,
  getNumericConfig,
  getNumericalProgressText,
  getDriveLinks,
  getProgressColorClass,
  isNumericalTask,
  isOngoingTask,
  isPositiveDecimal,
  normalizeDecimalValue,
  normalizeTaskType,
  toNumericInputValue,
} from '../lib/taskTypes';

const STATUS_FILTERS = new Set(['all', 'pending', 'in_progress', 'completed', 'overdue', 'ongoing']);

const normalizeStatusFilter = (rawStatus) => {
  if (!rawStatus) return 'all';
  const normalized = rawStatus
    .toString()
    .trim()
    .toLowerCase()
    .replace(/-/g, '_')
    .replace(/\s+/g, '_');

  if (normalized === 'inprogress') return 'in_progress';
  if (normalized === 'done') return 'completed';
  if (STATUS_FILTERS.has(normalized)) return normalized;
  return 'all';
};

const TasksPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUserId = user?.id || user?._id || '';
  const latestFetchIdRef = useRef(0);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersReady, setFiltersReady] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, taskId: null });
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [subtaskDialogOpen, setSubtaskDialogOpen] = useState(false);
  const [subtaskParentId, setSubtaskParentId] = useState(null);
  const [subtaskForm, setSubtaskForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    start_date: null,
    end_date: null,
    additional_assignees: [],
    contribution_value: '',
  });
  const [subtaskParentTask, setSubtaskParentTask] = useState(null);
  const [subtaskSubmitting, setSubtaskSubmitting] = useState(false);
  const [stagedAttachments, setStagedAttachments] = useState([]);
  const [stagedAttachmentProgress, setStagedAttachmentProgress] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    status: 'pending',
    task_type: TASK_TYPE_STANDARD,
    numeric_config: { ...DEFAULT_NUMERIC_CONFIG },
    primary_assignee: currentUserId,
    secondary_assignee: '',
    additional_assignees: [],
    start_date: null,
    end_date: null,
    percent_complete: 0,
    is_ongoing: false,
    drive_links: []
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const nextStatus = normalizeStatusFilter(params.get('status'));
    const mineOnly = params.get('mine') === 'true' || params.get('scope') === 'mine';

    if (mineOnly && !currentUserId) {
      setFiltersReady(false);
      return;
    }

    const nextAssignee = mineOnly && currentUserId ? currentUserId : 'all';

    setFilterStatus(nextStatus);
    setFilterAssignee(nextAssignee);
    setFilterPriority('all');
    setSearchQuery('');
    setPagination(prev => ({ ...prev, page: 1 }));
    setFiltersReady(true);
  }, [location.search, currentUserId]);

  useEffect(() => {
    if (!filtersReady) return;
    fetchData();
  }, [filtersReady, pagination.page, filterStatus, filterAssignee, filterPriority, searchQuery]);

  const fetchData = async () => {
    const fetchId = ++latestFetchIdRef.current;
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };
      if (searchQuery.trim()) params.search = searchQuery.trim();
      if (filterStatus === 'overdue') {
        params.overdue = true;
      } else if (filterStatus === 'ongoing') {
        params.ongoing = true;
      } else if (filterStatus !== 'all') {
        params.status = filterStatus;
      }
      if (filterAssignee !== 'all') params.assigned_to = filterAssignee;
      if (filterPriority !== 'all') params.priority = filterPriority;

      const [tasksRes, usersRes] = await Promise.all([
        api.get('/tasks', { params }),
        api.get('/users')
      ]);
      if (fetchId !== latestFetchIdRef.current) return;
      if (tasksRes.data.tasks && tasksRes.data.pagination) {
        setTasks(tasksRes.data.tasks);
        setPagination(tasksRes.data.pagination);
      } else {
        setTasks(tasksRes.data);
      }
      setUsers(usersRes.data);
    } catch (error) {
      if (fetchId !== latestFetchIdRef.current) return;
      toast.error('Failed to load tasks');
    } finally {
      if (fetchId === latestFetchIdRef.current) {
        setLoading(false);
      }
    }
  };

  const setPageOne = () => {
    setPagination(prev => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  };

  const openCreateDialog = () => {
    setEditingTask(null);
    setFormData({
      title: '',
      description: '',
      priority: 'medium',
      status: 'pending',
      task_type: TASK_TYPE_STANDARD,
      numeric_config: { ...DEFAULT_NUMERIC_CONFIG },
      primary_assignee: currentUserId,
      secondary_assignee: '',
      additional_assignees: [],
      start_date: null,
      end_date: null,
      percent_complete: 0,
      is_ongoing: false,
      drive_links: []
    });
    setStagedAttachments([]);
    setStagedAttachmentProgress({});
    setDialogOpen(true);
  };

  const openEditDialog = (task) => {
    const numericConfig = getNumericConfig(task);
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description,
      priority: task.priority,
      status: task.status,
      task_type: normalizeTaskType(task.task_type),
      numeric_config: {
        ...numericConfig,
        target_value: toNumericInputValue(numericConfig.target_value),
      },
      primary_assignee: task.primary_assignee || currentUserId,
      secondary_assignee: task.secondary_assignee || '',
      additional_assignees: task.additional_assignees || [],
      start_date: task.start_date ? new Date(task.start_date) : null,
      end_date: task.end_date ? new Date(task.end_date) : null,
      percent_complete: task.percent_complete || 0,
      is_ongoing: isOngoingTask(task),
      drive_links: getDriveLinks(task)
    });
    setStagedAttachments([]);
    setStagedAttachmentProgress({});
    setDialogOpen(true);
  };

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.primary_assignee) {
      toast.error('Primary owner is required');
      return;
    }

    if (isNumericalTask(formData.task_type) && !isPositiveDecimal(formData.numeric_config?.target_value)) {
      toast.error('Target amount must be greater than zero and have at most 2 decimal places');
      return;
    }

    // Validate dates are mandatory
    if (!formData.start_date) {
      toast.error('Start date is required');
      return;
    }
    const endLabel = formData.is_ongoing ? 'Till date' : 'End date';
    if (!formData.end_date) {
      toast.error(`${endLabel} is required`);
      return;
    }

    // Validate dates are not in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (!editingTask && formData.start_date && formData.start_date < today) {
      toast.error('Start date cannot be in the past');
      return;
    }
    if (!editingTask && formData.end_date && formData.end_date < today) {
      toast.error(`${endLabel} cannot be in the past`);
      return;
    }
    // Validate end_date >= start_date
    if (formData.start_date && formData.end_date && new Date(formData.end_date) < new Date(formData.start_date)) {
      toast.error(`${endLabel} must be on or after start date`);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        status: formData.status,
        task_type: normalizeTaskType(formData.task_type),
        start_date: formData.start_date ? formData.start_date.toISOString() : null,
        end_date: formData.end_date ? formData.end_date.toISOString() : null,
        additional_assignees: formData.additional_assignees || [],
        drive_links: formData.drive_links || [],
      };
      if (!isNumericalTask(formData.task_type)) {
        payload.is_ongoing = !!formData.is_ongoing;
      }
      const secondaryAssignee = formData.secondary_assignee && formData.secondary_assignee !== 'none'
        ? formData.secondary_assignee
        : null;
      if (!editingTask || formData.primary_assignee !== editingTask.primary_assignee) {
        payload.primary_assignee = formData.primary_assignee;
      }
      if (!editingTask || secondaryAssignee !== (editingTask.secondary_assignee || null)) {
        payload.secondary_assignee = secondaryAssignee;
      }
      if (isNumericalTask(formData.task_type)) {
        payload.numeric_config = buildNumericConfigPayload(formData.numeric_config);
      }
      // Preserve Standard Task behavior. Numerical progress is always server-calculated.
      if (!isNumericalTask(formData.task_type) && !formData.is_ongoing && !(editingTask && editingTask.subtask_count > 0)) {
        payload.percent_complete = formData.percent_complete || 0;
      }

      if (editingTask) {
        await api.put(`/tasks/${editingTask.id}`, payload);
        toast.success('Task updated');
      } else {
        const response = await api.post('/tasks', payload);
        const createdTaskId = response.data?.id || response.data?._id;
        toast.success('Task created');

        if (stagedAttachments.length > 0) {
          if (!createdTaskId) {
            toast.warning('Task created, but attachments could not be uploaded because the task ID was not returned');
          } else {
            // Upload one staged file at a time so task creation cannot create a
            // burst of upload-intent and completion requests.
            let failedCount = 0;
            for (let index = 0; index < stagedAttachments.length; index += 1) {
              try {
                await uploadTaskAttachment({
                  api,
                  taskId: createdTaskId,
                  file: stagedAttachments[index],
                  onProgress: progress => setStagedAttachmentProgress(current => ({ ...current, [index]: progress })),
                });
              } catch {
                failedCount += 1;
              }
            }
            if (failedCount > 0) {
              toast.warning(`Task created; ${failedCount} attachment${failedCount === 1 ? '' : 's'} failed to upload. You can retry from task details.`);
            } else {
              toast.success(`${stagedAttachments.length} attachment${stagedAttachments.length === 1 ? '' : 's'} uploaded`);
            }
          }
        }
      }
      setDialogOpen(false);
      setStagedAttachments([]);
      setStagedAttachmentProgress({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (taskId) => {
    setDeleteConfirm({ open: true, taskId });
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/tasks/${deleteConfirm.taskId}`);
      toast.success('Task deleted');
      setDeleteConfirm({ open: false, taskId: null });
      fetchData();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const openSubtaskDialog = (parentTask) => {
    setSubtaskParentId(parentTask.id);
    setSubtaskParentTask(parentTask);
    setSubtaskForm({
      title: '',
      description: '',
      priority: 'medium',
      start_date: null,
      end_date: null,
      additional_assignees: [],
      contribution_value: '',
    });
    setSubtaskDialogOpen(true);
  };

  const handleSubtaskSubmit = async (e) => {
    e.preventDefault();
    if (!subtaskForm.title.trim()) return;
    if (!subtaskForm.additional_assignees || subtaskForm.additional_assignees.length === 0) {
      toast.error('Please assign at least one person to the subtask');
      return;
    }
    if (isNumericalTask(subtaskParentTask) && !isPositiveDecimal(subtaskForm.contribution_value)) {
      toast.error('Contribution amount must be greater than zero and have at most 2 decimal places');
      return;
    }
    if (!subtaskForm.start_date) {
      toast.error('Start date is required');
      return;
    }
    if (!subtaskForm.end_date) {
      toast.error('End date is required');
      return;
    }
    if (subtaskForm.start_date && subtaskForm.end_date && new Date(subtaskForm.end_date) < new Date(subtaskForm.start_date)) {
      toast.error('End date must be on or after start date');
      return;
    }
    setSubtaskSubmitting(true);
    try {
      const payload = {
        title: subtaskForm.title,
        description: subtaskForm.description,
        priority: subtaskForm.priority,
        start_date: subtaskForm.start_date ? subtaskForm.start_date.toISOString() : null,
        end_date: subtaskForm.end_date ? subtaskForm.end_date.toISOString() : null,
        additional_assignees: subtaskForm.additional_assignees || [],
        parent_task: subtaskParentId,
      };
      if (isNumericalTask(subtaskParentTask)) {
        payload.contribution_value = normalizeDecimalValue(subtaskForm.contribution_value);
      }
      await api.post('/tasks', payload);
      toast.success('Subtask created');
      setSubtaskDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create subtask');
    } finally {
      setSubtaskSubmitting(false);
    }
  };

  const filteredTasks = tasks;

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'warning';
      case 'low': return 'secondary';
      default: return 'outline';
    }
  };

  // Filter out deactivated users for assignment dropdowns
  const activeUsers = users.filter(u => u.is_active !== false);

  // Users available for subtask "Assigned To" multi-select (all active users)
  const subtaskAvailableForAssignment = activeUsers;

  const toggleSubtaskAssignee = (userId) => {
    setSubtaskForm(prev => {
      const current = prev.additional_assignees || [];
      if (current.includes(userId)) {
        return { ...prev, additional_assignees: current.filter(id => id !== userId) };
      } else {
        return { ...prev, additional_assignees: [...current, userId] };
      }
    });
  };

  const primaryAssigneeId = formData.primary_assignee || currentUserId;
  const ownerOptions = users.filter(u => (
    u.is_active !== false || u.id === formData.primary_assignee || u.id === formData.secondary_assignee
  ));

  // Users available for "Assigned To" multi-select (exclude primary + secondary)
  const availableForAssignment = activeUsers.filter(u => {
    if (u.id === primaryAssigneeId) return false;
    if (formData.secondary_assignee && formData.secondary_assignee !== 'none' && u.id === formData.secondary_assignee) return false;
    return true;
  });

  const toggleAssignee = (userId) => {
    setFormData(prev => {
      const current = prev.additional_assignees || [];
      if (current.includes(userId)) {
        return { ...prev, additional_assignees: current.filter(id => id !== userId) };
      } else {
        return { ...prev, additional_assignees: [...current, userId] };
      }
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'default';
      case 'pending': return 'pending';
      default: return 'outline';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="tasks-loading">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl tracking-tight uppercase">TASKS</h1>
          <p className="text-muted-foreground text-sm">
            {(pagination.total || filteredTasks.length)} tasks total
          </p>
        </div>
        <Button onClick={openCreateDialog} data-testid="create-task-btn">
          <Plus className="h-4 w-4 mr-2" />
          NEW TASK
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-border">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search tasks..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPageOne();
                  }}
                  className="pl-9"
                  data-testid="task-search-input"
                />
              </div>
            </div>
            <Select
              value={filterStatus}
              onValueChange={(value) => {
                setFilterStatus(value);
                setPageOne();
              }}
            >
              <SelectTrigger className="w-full md:w-[180px]" data-testid="filter-status-select">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="ongoing">Ongoing</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filterPriority}
              onValueChange={(value) => {
                setFilterPriority(value);
                setPageOne();
              }}
            >
              <SelectTrigger className="w-full md:w-[180px]" data-testid="filter-priority-select">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filterAssignee}
              onValueChange={(value) => {
                setFilterAssignee(value);
                setPageOne();
              }}
            >
              <SelectTrigger className="w-full md:w-[180px]" data-testid="filter-assignee-select">
                <SelectValue placeholder="Owner / Assignee" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Members</SelectItem>
                {users.map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tasks List */}
      <div className="space-y-3">
        {filteredTasks.map(task => {
          const isOverdue = task.is_overdue === true;

          return (
            <Card
              key={task.id}
              className={`border-l-4 transition-colors ${
                isOverdue
                  ? 'border-l-destructive border-destructive/50 bg-destructive/5 hover:border-destructive/70'
                  : 'border-l-transparent border-border hover:border-primary/50'
              }`}
              data-testid={`task-card-${task.id}`}
            >
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3">
                      <h3 className={`font-medium ${isOverdue ? 'font-semibold text-foreground' : ''}`}>{task.title}</h3>
                      <Badge variant={getPriorityColor(task.priority)} className="text-[10px]">
                        {task.priority.toUpperCase()}
                      </Badge>
                      {isOverdue && (
                        <Badge
                          variant="outline"
                          className="text-[10px] gap-1 border-destructive/40 bg-destructive/10 text-destructive font-semibold"
                          data-testid={`task-overdue-${task.id}`}
                        >
                          <AlertTriangle className="h-3 w-3" />
                          OVERDUE
                        </Badge>
                      )}
                      {isNumericalTask(task) && (
                        <Badge variant="outline" className="text-[10px] gap-1">
                          <Target className="h-3 w-3" />
                          NUMERICAL
                        </Badge>
                      )}
                      {isOngoingTask(task) && (
                        <Badge variant="outline" className="text-[10px] gap-1 border-primary/40 text-primary" data-testid={`task-ongoing-${task.id}`}>
                          <InfinityIcon className="h-3 w-3" />
                          ONGOING
                        </Badge>
                      )}
                    </div>
                    {task.description && (
                      <p className={`text-sm line-clamp-2 ${isOverdue ? 'text-foreground/85' : 'text-muted-foreground'}`}>{task.description}</p>
                    )}
                    {isNumericalTask(task) && (
                      <p className="text-xs font-medium text-foreground" data-testid={`task-numerical-progress-${task.id}`}>
                        {getNumericalProgressText(task)}
                      </p>
                    )}
                    {/* Progress bar — hidden for ongoing tasks, which never complete */}
                    <div className="flex items-center gap-2 text-xs">
                      {isOngoingTask(task) ? (
                        <span className={`text-xs ${task.till_date_passed ? 'text-orange-500 font-medium' : 'text-muted-foreground'}`}>
                          {task.till_date_passed ? 'Till date passed — extend it from task details' : 'Continuous responsibility'}
                        </span>
                      ) : (
                        <div className="flex items-center gap-2 flex-1 max-w-[250px]">
                          <div className="h-1.5 bg-muted overflow-hidden border border-border rounded-full flex-1">
                            <div
                              className={`h-full rounded-full transition-all ${getProgressColorClass(task.percent_complete)}`}
                              style={{ width: `${clampProgressPercent(task.percent_complete)}%` }}
                            />
                          </div>
                          <span className="text-muted-foreground shrink-0">{formatProgressPercent(task.percent_complete)}%</span>
                        </div>
                      )}
                      {task.subtask_count > 0 && (
                        <div className="flex items-center gap-1 text-xs text-primary cursor-pointer hover:underline"
                          onClick={(e) => { e.stopPropagation(); navigate(`/tasks/${task.id}`); }}>
                          <ClipboardList className="h-3 w-3" />
                          <span>{task.subtask_count} subtask{task.subtask_count !== 1 ? 's' : ''}</span>
                        </div>
                      )}
                      {task.comments_count > 0 && (
                        <div className="flex items-center gap-1 text-primary shrink-0">
                          <MessageSquare className="h-3 w-3" />
                          {task.comments_count}
                        </div>
                      )}
                    </div>
                    {getDriveLinks(task).length > 0 && (
                      <DriveLinksList links={getDriveLinks(task)} compact />
                    )}
                    {task.secondary_assignee_name && (
                      <span className="text-xs text-muted-foreground">Secondary: {task.secondary_assignee_name}</span>
                    )}
                    {task.assigned_names && task.assigned_names.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1">
                        <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Assigned to:</span>
                        {task.assigned_names.map((name, i) => (
                          <Badge key={i} variant="outline" className="text-[9px] px-1.5 py-0">{name}</Badge>
                        ))}
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      {task.end_date && (
                        <span className={`flex items-center gap-1 ${isOverdue ? 'text-destructive font-medium' : ''}`}>
                          <CalendarIcon className="h-3 w-3" />
                          {isOngoingTask(task) ? 'Till ' : ''}{format(new Date(task.end_date), 'MMM d, yyyy')}
                        </span>
                      )}
                      <span>•</span>
                      <span>Created by {task.created_by_name || 'Unknown'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2">
                    <Badge variant={getStatusColor(task.status)} className="text-[10px] whitespace-nowrap px-2 py-1" data-testid={`task-status-${task.id}`}>
                      {task.status.replace('_', ' ').toUpperCase()}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      onClick={() => navigate(`/tasks/${task.id}`)}
                      data-testid={`view-task-${task.id}`}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      onClick={() => openSubtaskDialog(task)}
                      title="Add subtask"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      onClick={() => openEditDialog(task)}
                      data-testid={`edit-task-${task.id}`}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      onClick={() => handleDeleteClick(task.id)}
                      data-testid={`delete-task-${task.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {filteredTasks.length === 0 && (
          <Card className="border-border">
            <CardContent className="p-12 text-center">
              <div className="flex flex-col items-center gap-4">
                <div className="rounded-full bg-muted p-4">
                  <ClipboardList className="h-10 w-10 text-muted-foreground" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-lg font-medium">No tasks found</h3>
                  <p className="text-sm text-muted-foreground">
                    {searchQuery || filterStatus !== 'all' || filterAssignee !== 'all' || filterPriority !== 'all'
                      ? 'Try adjusting your search or filters'
                      : 'Get started by creating your first task'}
                  </p>
                </div>
                {!searchQuery && filterStatus === 'all' && filterAssignee === 'all' && filterPriority === 'all' && (
                  <Button onClick={openCreateDialog} data-testid="empty-create-task-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Create your first task
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Pagination Controls */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-4" data-testid="pagination-controls">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            disabled={pagination.page <= 1}
            data-testid="pagination-prev"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Prev
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {pagination.page} of {pagination.pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            disabled={pagination.page >= pagination.pages}
            data-testid="pagination-next"
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTask ? 'EDIT TASK' : 'CREATE TASK'}</DialogTitle>
            <DialogDescription>
              {editingTask ? 'Update the task details below' : 'Fill in the details for your new task'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">TITLE</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                required
                data-testid="task-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">DESCRIPTION</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                data-testid="task-description-input"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>TASK TYPE</Label>
                <Select
                  value={formData.task_type}
                  disabled={!!editingTask}
                  onValueChange={(value) => setFormData(prev => ({
                    ...prev,
                    task_type: normalizeTaskType(value),
                    is_ongoing: value === TASK_TYPE_NUMERICAL ? false : prev.is_ongoing,
                    numeric_config: value === TASK_TYPE_NUMERICAL
                      ? { ...DEFAULT_NUMERIC_CONFIG, ...prev.numeric_config }
                      : { ...DEFAULT_NUMERIC_CONFIG },
                  }))}
                >
                  <SelectTrigger data-testid="task-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={TASK_TYPE_STANDARD}>Standard Task</SelectItem>
                    <SelectItem value={TASK_TYPE_NUMERICAL}>Numerical Target Task</SelectItem>
                  </SelectContent>
                </Select>
                {editingTask && <p className="text-[10px] text-muted-foreground">Task type cannot be changed after creation.</p>}
              </div>
              {isNumericalTask(formData.task_type) && (
                <div className="space-y-2">
                  <Label>CURRENCY</Label>
                  <Select
                    value={formData.numeric_config?.currency_code || 'INR'}
                    onValueChange={(value) => setFormData(prev => ({
                      ...prev,
                      numeric_config: { ...prev.numeric_config, currency_code: value },
                    }))}
                  >
                    <SelectTrigger data-testid="task-currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {NUMERICAL_CURRENCY_OPTIONS.map(currency => (
                        <SelectItem key={currency.code} value={currency.code}>{currency.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {!isNumericalTask(formData.task_type) && (
              <div className="flex items-start gap-3 border border-border p-3" data-testid="task-ongoing-field">
                <Checkbox
                  id="task-ongoing"
                  checked={!!formData.is_ongoing}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_ongoing: checked === true }))}
                  className="mt-0.5"
                  data-testid="task-ongoing-checkbox"
                />
                <div className="space-y-0.5">
                  <Label htmlFor="task-ongoing" className="cursor-pointer">ONGOING TASK</Label>
                  <p className="text-xs text-muted-foreground">
                    A continuous responsibility (e.g. Portfolio Management). No % progress is tracked; set a Till Date and extend it whenever needed.
                  </p>
                </div>
              </div>
            )}
            {isNumericalTask(formData.task_type) && (
              <div className="space-y-2">
                <Label htmlFor="target-value">TARGET AMOUNT <span className="text-destructive">*</span></Label>
                <Input
                  id="target-value"
                  type="text"
                  inputMode="decimal"
                  pattern={"\\d+(\\.\\d{1,2})?"}
                  value={formData.numeric_config?.target_value || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    numeric_config: { ...prev.numeric_config, target_value: e.target.value },
                  }))}
                  placeholder="2000000"
                  required
                  data-testid="task-target-value-input"
                />
                {formData.numeric_config?.target_value && isPositiveDecimal(formData.numeric_config.target_value) && (
                  <p className="text-xs text-muted-foreground">
                    {formatNumericValue(formData.numeric_config.target_value, formData.numeric_config)}
                  </p>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>START DATE <span className="text-destructive">*</span></Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                      <span className="truncate">{formData.start_date ? format(formData.start_date, 'MMM d, yyyy') : 'Pick a date'}</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.start_date}
                      onSelect={(date) => {
                        setFormData(prev => {
                          const updated = { ...prev, start_date: date };
                          // Clear end_date if it's now before the new start_date
                          if (prev.end_date && date && prev.end_date < date) {
                            updated.end_date = null;
                          }
                          return updated;
                        });
                      }}
                      disabled={{ before: new Date() }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>{formData.is_ongoing ? 'TILL DATE' : 'END DATE'} <span className="text-destructive">*</span></Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="task-end-date-btn">
                      <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                      <span className="truncate">{formData.end_date ? format(formData.end_date, 'MMM d, yyyy') : 'Pick a date'}</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.end_date}
                      onSelect={(date) => setFormData(prev => ({ ...prev, end_date: date }))}
                      disabled={{ before: formData.start_date || new Date() }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>PRIORITY</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}
                >
                  <SelectTrigger data-testid="task-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>PRIMARY OWNER <span className="text-destructive">*</span></Label>
                <Select
                  value={formData.primary_assignee || ''}
                  onValueChange={(value) => setFormData(prev => ({
                    ...prev,
                    primary_assignee: value,
                    secondary_assignee: prev.secondary_assignee === value ? '' : prev.secondary_assignee,
                    additional_assignees: (prev.additional_assignees || []).filter(id => id !== value),
                  }))}
                >
                  <SelectTrigger data-testid="task-primary-assignee-select">
                    <SelectValue placeholder="Select owner" />
                  </SelectTrigger>
                  <SelectContent>
                    {ownerOptions.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}{u.is_active === false ? ' (Inactive)' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>SECONDARY OWNER</Label>
                <Select
                  value={formData.secondary_assignee}
                  onValueChange={(value) => {
                    setFormData(prev => {
                      const updated = { ...prev, secondary_assignee: value };
                      // Remove from additional_assignees if being set as secondary
                      if (value && value !== 'none') {
                        updated.additional_assignees = (prev.additional_assignees || []).filter(id => id !== value);
                      }
                      return updated;
                    });
                  }}
                >
                  <SelectTrigger data-testid="task-secondary-assignee-select">
                    <SelectValue placeholder="Select person" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {ownerOptions.filter(u => u.id !== primaryAssigneeId).map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}{u.is_active === false ? ' (Inactive)' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>ASSIGNED TO</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <Users className="mr-2 h-4 w-4 shrink-0" />
                      <span className="truncate">
                        {(formData.additional_assignees || []).length > 0
                          ? `${formData.additional_assignees.length} selected`
                          : 'Select people'}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[240px] p-0" align="start">
                    <div className="max-h-48 overflow-y-auto p-1">
                      {availableForAssignment.length === 0 ? (
                        <p className="text-xs text-muted-foreground p-2 text-center">No users available</p>
                      ) : (
                        availableForAssignment.map(u => (
                          <div
                            key={u.id}
                            className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-muted rounded-sm"
                            onClick={() => toggleAssignee(u.id)}
                          >
                            <div className={`h-4 w-4 border rounded-sm flex items-center justify-center shrink-0 ${
                              (formData.additional_assignees || []).includes(u.id) ? 'bg-primary border-primary' : 'border-muted-foreground'
                            }`}>
                              {(formData.additional_assignees || []).includes(u.id) && <Check className="h-3 w-3 text-primary-foreground" />}
                            </div>
                            <span className="text-sm">{u.name}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <DriveLinksEditor
              value={formData.drive_links || []}
              onChange={(links) => setFormData(prev => ({ ...prev, drive_links: links }))}
              disabled={submitting}
            />
            {!editingTask && (
              <StagedAttachmentPicker
                files={stagedAttachments}
                onChange={setStagedAttachments}
                disabled={submitting}
                progress={stagedAttachmentProgress}
              />
            )}
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setDialogOpen(false)}>
                CANCEL
              </Button>
              <Button type="submit" disabled={submitting} data-testid="task-submit-btn">
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingTask ? 'UPDATE' : 'CREATE'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Subtask Creation Dialog */}
      <Dialog open={subtaskDialogOpen} onOpenChange={setSubtaskDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>CREATE SUBTASK</DialogTitle>
            <DialogDescription>
              {isNumericalTask(subtaskParentTask)
                ? 'Add a numerical contribution to the selected task'
                : 'Add a subtask to the selected task'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubtaskSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="subtask-title">TITLE</Label>
              <Input
                id="subtask-title"
                value={subtaskForm.title}
                onChange={(e) => setSubtaskForm(prev => ({ ...prev, title: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="subtask-description">DESCRIPTION</Label>
              <Textarea
                id="subtask-description"
                value={subtaskForm.description}
                onChange={(e) => setSubtaskForm(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
            {isNumericalTask(subtaskParentTask) && (
              <div className="space-y-2">
                <Label htmlFor="subtask-contribution">CONTRIBUTION AMOUNT <span className="text-destructive">*</span></Label>
                <Input
                  id="subtask-contribution"
                  type="text"
                  inputMode="decimal"
                  pattern={"\\d+(\\.\\d{1,2})?"}
                  value={subtaskForm.contribution_value}
                  onChange={(e) => setSubtaskForm(prev => ({ ...prev, contribution_value: e.target.value }))}
                  placeholder="500000"
                  required
                  data-testid="subtask-contribution-input"
                />
                {isPositiveDecimal(subtaskForm.contribution_value) && (
                  <p className="text-xs text-muted-foreground">
                    {formatNumericValue(subtaskForm.contribution_value, getNumericConfig(subtaskParentTask))}
                  </p>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>START DATE <span className="text-destructive">*</span></Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                      <span className="truncate">{subtaskForm.start_date ? format(subtaskForm.start_date, 'MMM d, yyyy') : 'Pick a date'}</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={subtaskForm.start_date}
                      onSelect={(date) => {
                        setSubtaskForm(prev => {
                          const updated = { ...prev, start_date: date };
                          if (prev.end_date && date && prev.end_date < date) {
                            updated.end_date = null;
                          }
                          return updated;
                        });
                      }}
                      disabled={{ before: new Date() }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>END DATE <span className="text-destructive">*</span></Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                      <span className="truncate">{subtaskForm.end_date ? format(subtaskForm.end_date, 'MMM d, yyyy') : 'Pick a date'}</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={subtaskForm.end_date}
                      onSelect={(date) => setSubtaskForm(prev => ({ ...prev, end_date: date }))}
                      disabled={{ before: subtaskForm.start_date || new Date() }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>PRIORITY</Label>
                <Select
                  value={subtaskForm.priority}
                  onValueChange={(value) => setSubtaskForm(prev => ({ ...prev, priority: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>ASSIGNED TO</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <Users className="mr-2 h-4 w-4 shrink-0" />
                      <span className="truncate">
                        {(subtaskForm.additional_assignees || []).length > 0
                          ? `${subtaskForm.additional_assignees.length} selected`
                          : 'Select people'}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[240px] p-0" align="start">
                    <div className="max-h-48 overflow-y-auto p-1">
                      {subtaskAvailableForAssignment.length === 0 ? (
                        <p className="text-xs text-muted-foreground p-2 text-center">No users available</p>
                      ) : (
                        subtaskAvailableForAssignment.map(u => (
                          <div
                            key={u.id}
                            className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-muted rounded-sm"
                            onClick={() => toggleSubtaskAssignee(u.id)}
                          >
                            <div className={`h-4 w-4 border rounded-sm flex items-center justify-center shrink-0 ${
                              (subtaskForm.additional_assignees || []).includes(u.id) ? 'bg-primary border-primary' : 'border-muted-foreground'
                            }`}>
                              {(subtaskForm.additional_assignees || []).includes(u.id) && <Check className="h-3 w-3 text-primary-foreground" />}
                            </div>
                            <span className="text-sm">{u.name}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setSubtaskDialogOpen(false)}>
                CANCEL
              </Button>
              <Button type="submit" disabled={subtaskSubmitting}>
                {subtaskSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                CREATE
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => setDeleteConfirm({ open, taskId: open ? deleteConfirm.taskId : null })}
        title="Delete Task"
        description="Are you sure you want to delete this task? This action cannot be undone."
        confirmLabel="DELETE"
        onConfirm={handleDeleteConfirm}
        variant="destructive"
      />
    </div>
  );
};

export default TasksPage;
